import { useState } from 'react';
import { Inquiry } from '@/mocks/inquiries';

interface ReplyModalProps {
  inquiry: Inquiry;
  onClose: () => void;
  onReply: (id: string, reply: string) => void;
  notificationEmail?: string;
}

const ReplyModal = ({ inquiry, onClose, onReply, notificationEmail }: ReplyModalProps) => {
  const [reply, setReply] = useState(inquiry.reply || '');
  const [charCount, setCharCount] = useState(inquiry.reply?.length || 0);
  const [emailOpened, setEmailOpened] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReply(e.target.value);
    setCharCount(e.target.value.length);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reply.trim() && charCount <= 500) {
      onReply(inquiry.id, reply.trim());
    }
  };

  const handleOpenEmail = () => {
    const subject = encodeURIComponent(`Re: Your inquiry with Greenly Electronics Recycling`);
    const body = encodeURIComponent(reply.trim() ? reply : '');
    const mailtoLink = `mailto:${inquiry.email}?subject=${subject}&body=${body}`;
    window.open(mailtoLink, '_blank');
    setEmailOpened(true);
    if (reply.trim()) {
      onReply(inquiry.id, reply.trim());
    }
  };

  const handleOpenGmail = () => {
    const subject = encodeURIComponent(`Re: Your inquiry with Greenly Electronics Recycling`);
    const bodyText = reply.trim()
      ? reply
      : `Hi ${inquiry.name},\n\nThank you for reaching out to Greenly Electronics Recycling.\n\n`;
    const body = encodeURIComponent(bodyText);
    const authUser = notificationEmail ? `&authuser=${encodeURIComponent(notificationEmail)}` : '';
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(inquiry.email)}&su=${subject}&body=${body}${authUser}`;
    window.open(gmailUrl, '_blank');
    setEmailOpened(true);
    if (reply.trim()) {
      onReply(inquiry.id, reply.trim());
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-gray-900 text-lg">Reply to {inquiry.name}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full cursor-pointer text-gray-500">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* To field — pre-filled email */}
        <div className="flex items-center gap-3 bg-[#F4FAF7] border border-[#1B6B3A]/20 rounded-xl px-4 py-3 mb-4">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-widest shrink-0">To</span>
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 flex items-center justify-center bg-[#1B6B3A] rounded-full shrink-0">
              <i className="ri-user-line text-white text-xs"></i>
            </div>
            <span className="font-semibold text-gray-900 text-sm truncate">{inquiry.name}</span>
            <span className="text-[#1B6B3A] text-sm truncate">&lt;{inquiry.email}&gt;</span>
          </div>
          <a
            href={`mailto:${inquiry.email}`}
            className="w-7 h-7 flex items-center justify-center hover:bg-[#1B6B3A]/10 rounded-lg cursor-pointer transition-colors shrink-0"
            title="Copy email"
          >
            <i className="ri-file-copy-line text-[#1B6B3A] text-sm"></i>
          </a>
        </div>

        {/* Inquiry preview */}
        <div className="bg-gray-50 rounded-xl p-4 mb-5 border border-gray-100">
          {inquiry.phone && (
            <p className="text-sm text-gray-400 mb-2 flex items-center gap-1.5">
              <i className="ri-phone-line text-[#1B6B3A]"></i> {inquiry.phone}
            </p>
          )}
          <div className="border-t border-gray-200 pt-3 mt-1">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Their message</p>
            <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line line-clamp-4">{inquiry.message}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Your Reply
              <span className={`float-right font-normal text-sm ${charCount > 500 ? 'text-red-500' : 'text-gray-400'}`}>
                {charCount}/500
              </span>
            </label>
            <textarea
              value={reply}
              onChange={handleChange}
              rows={5}
              maxLength={500}
              placeholder="Type your reply here..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:border-[#1B6B3A] focus:ring-2 focus:ring-[#1B6B3A]/10 resize-none"
            />
            {charCount > 500 && <p className="text-red-500 text-sm mt-1">Reply cannot exceed 500 characters.</p>}
          </div>

          {/* Gmail — primary action */}
          <button
            type="button"
            onClick={handleOpenGmail}
            className="w-full flex items-center justify-center gap-2 bg-[#EA4335] hover:bg-[#c5372b] text-white font-bold py-3 rounded-xl cursor-pointer whitespace-nowrap text-sm transition-colors mb-2"
          >
            <i className="ri-google-line text-base"></i>
            Reply via Gmail — To: {inquiry.email}
            {notificationEmail && <span className="text-white/70 font-normal text-xs ml-1">from {notificationEmail}</span>}
          </button>

          {/* Open in default email client — secondary action */}
          <button
            type="button"
            onClick={handleOpenEmail}
            className="w-full flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-600 hover:border-[#1B6B3A] hover:text-[#1B6B3A] font-medium py-2.5 rounded-xl cursor-pointer whitespace-nowrap text-sm transition-colors mb-3"
          >
            <i className="ri-mail-send-line text-base"></i>
            Open in Default Email App
          </button>

          {emailOpened && (
            <p className="text-center text-xs text-[#1B6B3A] mb-3 flex items-center justify-center gap-1">
              <i className="ri-check-line"></i> Email client opened with {inquiry.email} pre-filled
            </p>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 border border-gray-200 text-gray-600 font-medium rounded-full hover:bg-gray-50 cursor-pointer whitespace-nowrap text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={charCount > 500 || !reply.trim()}
              className="px-5 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-full hover:bg-gray-200 disabled:opacity-40 cursor-pointer whitespace-nowrap text-sm transition-colors flex items-center gap-2"
            >
              <i className="ri-save-line"></i> Save Note Only
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReplyModal;
